import * as React from 'react';
import SelectionToggler from '../../../containers/Selection/Toggler';
import { ListDisplayType } from '../../../containers/DriveItemList/constants';
import { ResolutionDisplayType, ThumbnailDescriptor } from '../../../utils/driveItem';
import {
	GridImageWrapper,
	RowImageWrapper,
	BlockSvgWrapper,
	RowSvgWrapper,
	RowBackgroundImage,
	GridBackgroundImage,
	MasonryImage,
	MasonryImageWrapper,
	IconCheck,
} from './styles';
import Loader, { LoaderType, LoaderStepName, LoaderStep } from '../../Loader';
import Utils from '../../../utils';
import { SelectionType } from '../../../containers/Selection/actions';

interface ListItemThumbnailProps {
	selectionId?: number;
	thumbnailDescriptor: ThumbnailDescriptor;
	displayType: ListDisplayType;
	isActivated: boolean;
	hasSelectionIcon: boolean;
	selectionType?: SelectionType;
	isMultipleSelection?: boolean;
	isSelectedInMultipleSelection?: boolean;
	isDownloading: boolean;
	isDownloadInProgress: boolean;
	isUpdating: boolean;
	isDeletionInProgress: boolean;
	isMarkingItemForDeletion: boolean;
	loaderStep: LoaderStep | undefined;
}

const favElement = (props: ListItemThumbnailProps) => {
	const { hasSelectionIcon, selectionId, displayType, thumbnailDescriptor, selectionType } = props;
	const { isImage } = thumbnailDescriptor;

	return hasSelectionIcon && selectionId ? (
		<SelectionToggler
			itemId={selectionId}
			displayMode={ResolutionDisplayType.Card}
			needBottomPadding={isImage && displayType === ListDisplayType.Masonry}
			isRowView={displayType === ListDisplayType.Row}
			selectionType={selectionType}
		/>
	) : null;
};

const selectElement = (props: ListItemThumbnailProps) => {
	const { isMultipleSelection, isSelectedInMultipleSelection } = props;

	if (!isMultipleSelection) {
		return null;
	}

	return (
		<IconCheck isChecked={isSelectedInMultipleSelection}>
			{isSelectedInMultipleSelection && Utils.getSvgIcon('iconset_check-3-bold')}
		</IconCheck>
	);
};

const loaderElement = (props: ListItemThumbnailProps) => {
	const { isDownloading, loaderStep, isDownloadInProgress, isUpdating, isDeletionInProgress, isMarkingItemForDeletion } = props;

	if (!isDownloading && !isDeletionInProgress && !isMarkingItemForDeletion) {
		return null;
	}

	return (
		<Loader
			loaderStep={loaderStep ? loaderStep : { step: LoaderStepName.Downloading }}
			type={LoaderType.ListItem}
			isDownloadInProgress={isDownloadInProgress}
			isUpdating={isUpdating}
			isDeletionInProgress={isDeletionInProgress}
			isMarkingItemForDeletion={isMarkingItemForDeletion}
		/>
	);
};

const getImageComponent = (displayType: ListDisplayType, isWrapper: boolean) => {
	switch (displayType) {
		case ListDisplayType.Row:
			return isWrapper ? RowImageWrapper : RowBackgroundImage;
		case ListDisplayType.Grid:
		case ListDisplayType.Coverflow:
			return isWrapper ? GridImageWrapper : GridBackgroundImage;
		case ListDisplayType.Masonry:
			return isWrapper ? MasonryImageWrapper : MasonryImage;
	}
};

const ImageComponent = (props: ListItemThumbnailProps) => {
	const { displayType, thumbnailDescriptor, isActivated } = props;
	const { src, fallbackSrc } = thumbnailDescriptor;
	const imageRef = React.useRef<HTMLDivElement>(null);

	const ImageWrapper = getImageComponent(displayType, true);
	const StyledImage = getImageComponent(displayType, false);

	// Preload images for non-Masonry/backgroundImage types to trigger error handler
	React.useEffect(() => {
		if (displayType !== ListDisplayType.Masonry && fallbackSrc && fallbackSrc !== src) {
			const img = new Image();
			img.src = src;

			img.onerror = () => {
				if (imageRef.current && fallbackSrc) {
					imageRef.current.style.backgroundImage = `url('${fallbackSrc}')`;
				}
			};
		}
	}, [src, fallbackSrc, displayType]);

	return (
		<ImageWrapper isActivated={isActivated} className="ImageWrapper">
			{displayType === ListDisplayType.Masonry ? (
				<StyledImage
					src={src}
					onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
						const target = e.target as HTMLImageElement;

						if (fallbackSrc && target.src !== fallbackSrc) {
							target.src = fallbackSrc;
						}
					}}
				/>
			) : (
				<StyledImage
					ref={imageRef}
					imgURL={src}
				/>
			)}
			{favElement(props)}
			{selectElement(props)}
			{loaderElement(props)}
		</ImageWrapper>
	);
};

const SvgComponent = (props: ListItemThumbnailProps) => {
	const { displayType, thumbnailDescriptor } = props;
	const { src } = thumbnailDescriptor;
	const SvgWrapper = displayType !== ListDisplayType.Row ? BlockSvgWrapper : RowSvgWrapper;

	return (
		<SvgWrapper className="SvgWrapper">
			{Utils.getSvgIcon(src)}
			{favElement(props)}
			{selectElement(props)}
			{loaderElement(props)}
		</SvgWrapper>
	);
};

const ListItemThumbnail = (props: ListItemThumbnailProps) => {
	const { thumbnailDescriptor } = props;
	const { isImage } = thumbnailDescriptor;

	return isImage ? <ImageComponent {...props} /> : <SvgComponent {...props} />;
};

export default ListItemThumbnail;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ListItem/Thumbnail/index.tsx